import { useState, useMemo, lazy, Suspense, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatDate, type Certificate, type AuthUser, type Course, type CourseEdition } from "@/lib/api";
import EditStudentProfileModal from "@/components/feature/EditStudentProfileModal";
import ListingFilters from "@/components/feature/ListingFilters";
import { emptyListingFilters, matchesMonth, type ListingFilterState } from "@/lib/listingFilters";

const CertificatePreview = lazy(() => import("@/components/feature/CertificatePreview"));

export default function CertificadosPage() {
  const [filters, setFilters] = useState<ListingFilterState>(emptyListingFilters);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [editions, setEditions] = useState<CourseEdition[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewCert, setPreviewCert] = useState<Certificate | null>(null);
  const [qrCert, setQrCert] = useState<Certificate | null>(null);
  const [editStudent, setEditStudent] = useState<AuthUser | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [certs, coursesData, editionsData] = await Promise.all([
        api.getCertificates(),
        api.getCourses(),
        api.getEditions(),
      ]);
      setCertificates(certs);
      setCourses(Array.from(new Map([...coursesData, ...editionsData.map((ed) => ed.course).filter(Boolean) as Course[]].map((course) => [course.id, course])).values()));
      setEditions(editionsData);
    } catch {
      setCertificates([]);
      setCourses([]);
      setEditions([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return certificates.filter((c) => {
      const serviceId = c.course_id || c.diplomado_id;
      const editionId = c.edition_id || c.diplomado_edition_id;
      const service = c.edition?.course || c.diplomadoEdition?.diplomado;
      const edition = c.edition || c.diplomadoEdition;
      if (filters.courseId && serviceId !== filters.courseId) return false;
      if (filters.editionId && editionId !== filters.editionId) return false;
      if (filters.category && service?.category !== filters.category) return false;
      if (filters.month && !matchesMonth(c.issue_date, filters.month)) return false;
      if (q) {
        const haystack = [
          c.certificate_number,
          c.student?.full_name,
          service?.name,
          edition?.edition_label,
          service?.category,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [certificates, filters]);

  const activeCount = filtered.filter((c) => c.status === "activo").length;

  const handleRevoke = async (id: string) => {
    if (!confirm("¿Revocar este certificado?")) return;
    await api.revokeCertificate(id);
    load();
  };

  const handleResend = async (id: string) => {
    if (!confirm("¿Reenviar este certificado por correo al estudiante?")) return;
    try {
      await api.resendCertificate(id);
      alert("Certificado reenviado correctamente.");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Error al reenviar");
    }
  };

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Certificados</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Gestión de certificados emitidos por KSM Training
          </p>
        </div>
        <span className="badge-success">{activeCount} certificados activos</span>
      </div>

      {!loading && certificates.length > 0 && (
        <ListingFilters
          title="Filtros de certificados"
          filters={filters}
          onChange={setFilters}
          courses={courses}
          editions={editions}
          searchPlaceholder="Número, estudiante o curso..."
          resultCount={filtered.length}
          totalCount={certificates.length}
          hint="Mes filtra por fecha de emisión del certificado."
        />
      )}

      {loading ? (
        <div className="card text-center py-16">
          <i className="ri-loader-4-line text-3xl text-[var(--color-text-muted)] animate-spin"></i>
          <p className="text-sm text-[var(--color-text-muted)] mt-3">Cargando certificados...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-16">
          <i className="ri-award-line text-4xl text-[var(--color-text-muted)]"></i>
          <p className="text-sm text-[var(--color-text-muted)] mt-3">
            No se encontraron certificados
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((cert) => {
            const studentName = cert.student?.full_name || "Estudiante";
            const service = cert.edition?.course || cert.diplomadoEdition?.diplomado;
            const edition = cert.edition || cert.diplomadoEdition;
            const courseName = service?.name || "Servicio";

            return (
              <div key={cert.id} className="card hover:border-[var(--color-border-light)] transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[var(--color-accent-gold-muted)] flex items-center justify-center flex-shrink-0 border border-[var(--color-accent-gold)]/20">
                    <i className="ri-award-line text-2xl text-[var(--color-text-gold)]"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                        {cert.certificate_number}
                      </h3>
                      {cert.status === "activo" ? (
                        <span className="badge-success text-[10px]">Activo</span>
                      ) : (
                        <span className="badge-danger text-[10px]">{cert.status}</span>
                      )}
                    </div>
                    <p className="text-sm text-[var(--color-text-secondary)]">{studentName}</p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{courseName}</p>
                    {edition?.edition_label && (
                      <p className="text-xs text-[var(--color-text-accent)] mt-0.5">{edition.edition_label}</p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-xs text-[var(--color-text-muted)]">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {cert.hours_completed}h
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {formatDate(cert.issue_date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <span className="text-[var(--color-text-muted)]">
                        Instructor: <span className="text-[var(--color-text-secondary)]">{cert.instructor_signature}</span>
                      </span>
                      <span className="text-[var(--color-text-muted)]">
                        Evaluador: <span className="text-[var(--color-text-secondary)]">{cert.evaluator_signature}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[var(--color-border)] flex-wrap">
                  <button onClick={() => setPreviewCert(cert)} className="btn-secondary text-xs py-1.5 px-3 cursor-pointer">
                    <i className="ri-eye-line"></i> Ver / Imprimir
                  </button>
                  {cert.student && (
                    <button
                      onClick={() =>
                        setEditStudent({
                          id: cert.student!.id,
                          full_name: cert.student!.full_name,
                          email: cert.student!.email || "",
                          role: "estudiante",
                          company: cert.student!.company || "",
                          position: cert.student!.position || "",
                          phone: cert.student!.phone || "",
                          can_evaluate: false,
                          credit_balance: 0,
                        })
                      }
                      className="btn-secondary text-xs py-1.5 px-3 cursor-pointer"
                      title="Corregir nombre para el certificado"
                    >
                      <i className="ri-edit-line"></i> Corregir nombre
                    </button>
                  )}
                  {cert.student_id && (
                    <Link to={`/estudiantes/${cert.student_id}`} className="btn-secondary text-xs py-1.5 px-3">
                      <i className="ri-user-line"></i> Perfil
                    </Link>
                  )}
                  {cert.qr_code && (
                    <button onClick={() => setQrCert(cert)} className="btn-secondary text-xs py-1.5 px-3 cursor-pointer">
                      <i className="ri-qr-code-line"></i> Ver QR
                    </button>
                  )}
                  {cert.status === "activo" && (
                    <>
                      <button onClick={() => handleResend(cert.id)} className="btn-secondary text-xs py-1.5 px-3 cursor-pointer">
                        <i className="ri-mail-send-line"></i> Reenviar
                      </button>
                      <button onClick={() => handleRevoke(cert.id)} className="text-xs text-red-400 hover:underline cursor-pointer ml-auto">
                        Revocar
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {previewCert && (
        <Suspense fallback={null}>
          <CertificatePreview
            cert={previewCert}
            studentName={previewCert.student?.full_name}
            courseName={previewCert.edition?.course?.name || previewCert.diplomadoEdition?.diplomado?.name}
            courseCategory={previewCert.edition?.course?.category || previewCert.diplomadoEdition?.diplomado?.category}
            courseDuration={previewCert.edition?.course?.duration || previewCert.diplomadoEdition?.diplomado?.duration}
            onClose={() => setPreviewCert(null)}
          />
        </Suspense>
      )}

      {qrCert?.qr_code && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setQrCert(null)}>
          <div className="card text-center p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-4">{qrCert.certificate_number}</h3>
            <img src={qrCert.qr_code} alt="QR" className="w-48 h-48 mx-auto" />
            <button onClick={() => setQrCert(null)} className="btn-secondary mt-6 cursor-pointer">Cerrar</button>
          </div>
        </div>
      )}

      {editStudent && (
        <EditStudentProfileModal
          student={editStudent}
          onClose={() => setEditStudent(null)}
          onSaved={() => {
            setEditStudent(null);
            load();
          }}
        />
      )}
    </div>
  );
}
